import {
  Avatar,
  Badge,
  colors,
  Divider,
  Drawer,
  Fade,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'theme/makeStyles';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'i18n';
import { useSelector } from 'react-redux';
import getInitials from 'utils/general/getInitials';
import Chat from '../Chat';
import Searchbar from '../components/Searchbar';
import SuperScrollbar from 'components/general/SuperScrollbar';
import dayjs from 'dayjs';
import { useUsersNoCustomer } from 'store/customSelectors';

const drawerWidth = 380;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 75
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: '0px 12px',
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },

  profile: {
    display: 'flex',
    alignItems: 'center',
  },
  badge: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
  badgeDot: {
    height: 9,
    minWidth: 9
  },
  onlineBadge: {
    backgroundColor: colors.green[600]
  },
  awayBadge: {
    backgroundColor: colors.orange[600]
  },
  busyBadge: {
    backgroundColor: colors.red[600]
  },
  offlineBadge: {
    backgroundColor: colors.grey[300]
  },
  unreadMessagesBadgeLight: {
    backgroundColor: '#263238',
    color: '#e0e0e0',
  },
  unreadMessagesBadgeDark: {
    backgroundColor: '#e0e0e0',
    color: '#263238',
  },
  details: {
    marginLeft: '18px',
    maxWidth: 300
  },
  chatTransition: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  avatar: {
    cursor: 'pointer',
    width: 40,
    height: 40,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    filter: 'grayscale(0%)',
    transition: `filter ${theme.transitions.duration.enteringScreen}ms`
  },
  avatarMenuInactive: {
    filter: 'grayscale(100%) !important'
  },
  contentWrapper: {
    opacity: 1,
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  contentWrapperInactive: {
    opacity: '0.2 !important',
    pointerEvents: 'none'
  }
}));

const RenderRow = ({ user, selectedUser, handleSelectUser, isOnline, unreadMessages, presenceOpen, menuInactive }) => {
  const { classes } = useStyles();
  const avatars = useSelector(state => state.misc.avatars);
  const { t } = useTranslation();
  const theme = useTheme();

  const typingUsers = useSelector(state => state.misc.typing);
  const activity = useMemo(() => {
    let activity = "lang.online";
    typingUsers.forEach((user_id) => {
      if (user_id === user.id) {
        activity = "lang.typing";
      }
    });
    return activity;
  }, [typingUsers]);

  const status = useMemo(() => {
    if (isOnline) { return 'online'; }
    return 'offline';
  }, []);

  return (
    <div style={{ padding: '5px 17px' }}>
      <div className={classes.profile}
        onClick={() => handleSelectUser(user)}
        style={{
          cursor: 'pointer',
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          classes={{
            dot: classes.badgeDot,
            badge: clsx({
              [classes.badge]: true,
              [classes.onlineBadge]: status === 'online',
              [classes.awayBadge]: status === 'away',
              [classes.busyBadge]: status === 'busy',
              [classes.offlineBadge]: status === 'offline'
            })
          }}
          variant="dot"
        >
          <Badge
            overlap="circular"
            badgeContent={unreadMessages.length}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            classes={{
              dot: classes.badgeDot,
              badge: clsx({
                [classes.badge]: true,
                [classes.unreadMessagesBadgeLight]: theme.name === "LIGHT" && true, //??
                [classes.unreadMessagesBadgeDark]: theme.name === "DARK" && true, //??
              })
            }}
          >
            
            
            {presenceOpen ?
              <Avatar
                className={!selectedUser.id ? clsx(classes.avatar, menuInactive && classes.avatarMenuInactive) : null}
                src={avatars[user.id] && `data:image/jpeg;base64,${avatars[user.id]}`}
              >
                {getInitials(user.name)}
              </Avatar>
              :
              <Tooltip
                title={user.name}
                placement='left'
              >
                <Avatar
                  className={!selectedUser.id ? clsx(classes.avatar, menuInactive && classes.avatarMenuInactive) : null}
                  src={avatars[user.id] && `data:image/jpeg;base64,${avatars[user.id]}`}
                >
                  {getInitials(user.name)}
                </Avatar>
              </Tooltip>
            }
          </Badge>
        </Badge>
        <div className={classes.details}>
          <Typography
            style={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
            variant="h5"
            color="textPrimary"
            underline="none">
            {user.name}
          </Typography>

          <Typography variant="body2" style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            color: '#b0b0b0'
          }}>
            {isOnline ? t(activity) : user.last_seen ? dayjs(user.last_seen).fromNow() : '-'}
          </Typography>
        </div>
      </div>
    </div>
  );
}

const RenderSelectedUser = ({ selectedUser, setSelectedUser, setPresenceOpen, menuInactive }) => {
  const { classes } = useStyles();
  const avatars = useSelector(state => state.misc.avatars);
  const presence = useSelector(state => state.misc.presence);
  const {t} = useTranslation();
  const typingUsers = useSelector(state => state.misc.typing);

  const selectedUserOnline = useMemo(() => {
    if (selectedUser.id) {
      let finalUser = presence.find((user) => user.id === selectedUser.id);
      if (finalUser) {
        return true;
      }
    }
    return false;
  }, [selectedUser, presence]);

  const status = useMemo(() => {
    if(!selectedUserOnline){
      return 'lang.offline'
    }
    let activity = "lang.online";
    typingUsers.forEach((user_id) => {
      if (user_id === selectedUser.id) {
        activity = "lang.typing";
      }
    });
    return activity;
  }, [typingUsers, selectedUser, selectedUserOnline]);

  return (
    <div className={classes.profile} style={{
      padding: '8px 16px',
    }}>
      <Badge
        overlap="circular"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        classes={selectedUserOnline ? {
          dot: classes.badgeDot,
          badge: clsx({
            [classes.badge]: true,
            [classes.onlineBadge]: true,
          })
        } : {
            dot: classes.badgeDot,
            badge: clsx({
              [classes.badge]: true,
              [classes.offlineBadge]: true,
            })
          }}
        variant="dot"
      >
        <Avatar
          className={clsx(classes.avatar, menuInactive && classes.avatarMenuInactive) }
          src={avatars[selectedUser.id] && `data:image/jpeg;base64,${avatars[selectedUser.id]}`}
          style={{
            margin: '4px 0px',
          }}
        >
          {getInitials(selectedUser.name)}
        </Avatar>
      </Badge>
      <div className={classes.details} style={{
        width: 245
      }}>
        <Typography
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
          variant="h5"
          color="textPrimary"
          underline="none">
          {selectedUser.name}
        </Typography>

        <Typography variant="body2" style={{
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          color: '#b0b0b0'
        }}>
          {selectedUserOnline ? t(status) : selectedUser.last_seen ? dayjs(selectedUser.last_seen).fromNow() : '-'}
        </Typography>
      </div>
      <IconButton
        onClick={() => {
          setSelectedUser({});
          setPresenceOpen(false);
        }}
        style={{
          // marginRight: '12px',
          float: 'right',
          zIndex: 99
        }}
        size="large">
        <CloseOutlinedIcon />
      </IconButton>
    </div>
  );
}

const MenuInactiveWrapper = ({ 
  children, 
  menuInactive 
}) => {
  const { classes } = useStyles();

  const [menuInactiveView, setMenuInactiveView] = useState(menuInactive);

  useEffect(() => {
    if(!menuInactive){
      setTimeout(() => {
        setMenuInactiveView(false);
      }, 295);
    }else{
      setMenuInactiveView(true);
    }
  }, [menuInactive]);

  if(menuInactiveView){
    return (
      <div className={clsx(classes.contentWrapper, menuInactive && classes.contentWrapperInactive )}>
        {children}
      </div>
    )
  }
  return children;
}

export default function DesktopDrawer({ 
  presenceOpen, 
  setPresenceOpen, 
  selectedUser, 
  setSelectedUser, 
  menuInactive
}){
  const { classes } = useStyles();
  const currentUser = useSelector(state => state.general.user);
  const presence = useSelector(state => state.misc.presence);
  const allUsersState = useUsersNoCustomer();
  const messageLists = useSelector(state => state.chatMessages);
  const allUsers = useMemo(() => allUsersState.map((user) => ({
    ...user,
    name: user.contact.name
  })), [allUsersState]);


  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if(selectedUser.id && !presenceOpen){
      setPresenceOpen(true);
    }
  }, [selectedUser]);

  const filteredPresenceUsers = useMemo(() => {
    return presence.filter((user) => {
      let userExists = false;
      allUsers.forEach((val) => {
        if(val.id === user.id){
          userExists = true;
        }
      });

      if(!userExists){return false;}

      if (user.id === currentUser.contact.id) { return false; }
      if (searchQuery.length) {
        if (!user.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, [searchQuery, presence, currentUser, selectedUser]);

  const sortedPresenceUsers = useMemo(() => filteredPresenceUsers.sort((a, b) => {
    if (a.name > b.name) { return 1; }
    if (a.name < b.name) { return -1; }
    return 0;
  }), [filteredPresenceUsers]);

  const filteredUsers = useMemo(() => {
    return allUsers.filter((user) => {
      if (user.id === currentUser.id) { return false; }
      let userPresent = false;
      filteredPresenceUsers.forEach((presenceUser) => {
        if (user.id === presenceUser.id) {
          userPresent = true;
        }
      });
      if (userPresent) { return false; }
      if (searchQuery.length) {
        if (!user.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, [searchQuery, allUsers, filteredPresenceUsers]);
  const sortedUsers = useMemo(() => filteredUsers.sort((a, b) => {
    if (a.name > b.name) { return 1; }
    if (a.name < b.name) { return -1; }
    return 0;
  }), [filteredUsers]);

  const unreadMessages = useMemo(() => {
    let tmpUnreadMessages = {};
    Object.keys(messageLists).forEach((msgListKey) => {
      messageLists[msgListKey].forEach((msg) => {
        if (!msg.read && msg.sender_id !== currentUser.contact.id) {
          if (!tmpUnreadMessages[msg.sender_id]) {
            tmpUnreadMessages[msg.sender_id] = [];
          }
          tmpUnreadMessages[msg.sender_id].push(msg);
        }
      })
    });
    return tmpUnreadMessages;
  }, [messageLists]);


  const handleDrawerToggle = () => {
    if(selectedUser.id){
      setSelectedUser({});
    }
    setPresenceOpen((prevState) => !prevState);
  }
  
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setPresenceOpen(true);
  }
  
  return (
    <div className={classes.root}>
      <Drawer
        anchor="right"
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: presenceOpen,
          [classes.drawerClose]: !presenceOpen,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: presenceOpen,
            [classes.drawerClose]: !presenceOpen,
          }),
        }}
      >
        <MenuInactiveWrapper menuInactive={menuInactive}>
        {!selectedUser.id ?
          <div className={classes.toolbar}>
            <IconButton onClick={handleDrawerToggle} size="large">
              {presenceOpen ? <ChevronRightIcon /> : <ChatOutlinedIcon /> }
              {/* <ChevronLeftIcon/> */}
            </IconButton>
            <Fade
              in={presenceOpen}
              unmountOnExit={true}
            >
              <div style={{
                display: 'inline-flex',
                width: '100%',
                padding: 16
              }}>
                <Searchbar
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                />
              </div>
            </Fade>
          </div>
        :
          <RenderSelectedUser
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            setPresenceOpen={setPresenceOpen}
            menuInactive={menuInactive}
          />
        }
        <Divider />

        {!selectedUser.id ?
          <SuperScrollbar>
            <div style={{
              marginTop: '10px',
              marginBottom: '20px',
              overflowX: 'hidden'
            }}>

              {sortedPresenceUsers.map((user) => (
                <RenderRow
                  key={user.id}
                  user={user}
                  selectedUser={selectedUser}
                  // setSelectedUser={setSelectedUser}
                  handleSelectUser={handleSelectUser}
                  isOnline={true}
                  unreadMessages={unreadMessages[user.id] ? unreadMessages[user.id] : []}
                  presenceOpen={presenceOpen}
                  menuInactive={menuInactive}
                />
              ))}
              {sortedUsers.map((user) => (
                <RenderRow
                  key={user.id}
                  user={user}
                  selectedUser={selectedUser}
                  // setSelectedUser={setSelectedUser}
                  handleSelectUser={handleSelectUser}
                  isOnline={false}
                  unreadMessages={unreadMessages[user.id] ? unreadMessages[user.id] : []}
                  presenceOpen={presenceOpen}
                  menuInactive={menuInactive}
                />
              ))}
            </div>
          </SuperScrollbar>
        :
          // <div>CONTENT HERE</div>
          // presenceOpen && selectedUser.id && 
          <Chat
            // className={classes.chatTransition}
            receiver={selectedUser} 
            presenceOpen={presenceOpen}
          />
        }
        </MenuInactiveWrapper>
      </Drawer>
    </div>
  );
}
